import { useCallback, useEffect, useState } from 'react';
import {
  getReglages,
  definirReglage,
  definirLangue,
  definirStyleSaison,
  definirPlacesAffluence,
  definirOffreLancement,
  synchroniserCatalogueStripe,
  getFilesPlanches,
  viderFilesPlanches,
  getEtatBase,
} from '../lib/api/adminApi';
import { oublierReglages } from '../lib/storage/modeTest';
import { SAISONS } from '../lib/storage/styleSite';
import Loader from './Loader';

/**
 * Les interrupteurs du produit.
 *
 * Un écran d'exploitation, pas de configuration : ce qu'on bascule ici change
 * immédiatement ce que voit un visiteur, y compris sur la page de connexion —
 * qui est servie par un autre serveur. D'où les notes sous chaque interrupteur :
 * un administrateur doit savoir jusqu'où porte son clic.
 */

/**
 * Un interrupteur et ce qu'il commande.
 *
 * Extrait dès le second : deux blocs recopiés divergent toujours — on corrige
 * la formulation de l'un, on oublie l'autre, et l'écran finit par se
 * contredire lui-même.
 */
// Exporté : la programmation des courriels automatiques s'en sert aussi, et
// une copie divergerait de celui-ci au premier changement.
/**
 * Le nombre de places de la salle d'attente.
 *
 * UN CHAMP À PART, ET NON UN RÉGLAGE DE PLUS SUR L'INTERRUPTEUR. Allumer la
 * salle est un geste d'urgence ; choisir son plafond est une décision qui
 * demande un chiffre qu'on n'a pas sous la main. Les séparer permet de
 * préparer l'un à froid et de n'avoir que l'autre à trouver dans la panique.
 *
 * LE BOUTON RESTE ÉTEINT TANT QUE RIEN N'A CHANGÉ : un « Enregistrer »
 * toujours cliquable laisse croire qu'on a oublié de valider.
 */
function PlafondAffluence({ places, onEnregistrer, occupe }) {
  const [valeur, setValeur] = useState(String(places));

  // Le champ suit la valeur du serveur quand elle arrive. Sans cet effet, la
  // lecture initiale — qui arrive après le premier rendu — laisserait le
  // champ sur sa valeur par défaut, et l'administrateur enregistrerait 300
  // en croyant confirmer ce qui est déjà réglé.
  useEffect(() => { setValeur(String(places)); }, [places]);

  const nombre = Number.parseInt(valeur, 10);
  const valide = Number.isFinite(nombre) && nombre >= 1 && nombre <= 100000;
  const change = valide && nombre !== places;

  return (
    <div className="mode mode--reglable">
      <div className="mode__texte">
        <strong className="mode__titre">Places simultanées</strong>

        <p className="mode__description">
          Combien de visiteurs le site accepte en même temps quand la salle
          d&apos;attente est allumée. Au-delà, les suivants prennent un rang et
          entrent à mesure que des places se libèrent.
        </p>

        <p className="mode__note">
          CE NOMBRE NE SE DEVINE PAS. Trop bas, il fait patienter des parents
          que la machine aurait servis sans peine&nbsp;; trop haut, il ne
          protège de rien. Seul un test de charge le donne — il n&apos;a pas
          encore eu lieu, et 300 n&apos;est qu&apos;un point de départ prudent.
        </p>

        <div className="mode__champs">
          <div className="champ">
            <label htmlFor="affluence-places">Nombre de places</label>
            <input
              id="affluence-places"
              type="number"
              min="1"
              max="100000"
              inputMode="numeric"
              value={valeur}
              onChange={(e) => setValeur(e.target.value)}
            />
            <span className="champ__aide">
              Entre 1 et 100 000. Baisser ce nombre ne met personne dehors&nbsp;:
              le plafond se fait sentir à mesure que les visiteurs partent.
            </span>
          </div>
        </div>

        <div className="mode__actions">
          {/* ÉTEINT TANT QUE RIEN N'A CHANGÉ. Un bouton toujours cliquable
              laisse croire qu'on a oublié de valider, et on reclique. */}
          <button
            type="button"
            className="btn btn--compact btn--fantome"
            disabled={!change || occupe}
            onClick={() => onEnregistrer(nombre)}
          >
            {occupe ? 'Enregistrement…' : 'Enregistrer le nombre de places'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Interrupteur({
  titre, actif, connu, occupe, onBasculer, description, effets, note, children,
}) {
  return (
    <div className="mode">
      <div className="mode__texte">
        <strong className="mode__titre">
          {titre}

          {/* « État inconnu » et non « Désactivé » quand la lecture a échoué :
              afficher un état qu'on n'a pas lu, c'est l'inventer. Un
              administrateur qui voit « désactivé » en conclut que c'est éteint,
              alors que ce peut très bien être allumé. */}
          <span className={`mode__etat ${connu && actif ? 'mode__etat--actif' : ''}`}>
            {!connu ? 'État inconnu' : actif ? 'Activé' : 'Désactivé'}
          </span>
        </strong>

        <p className="mode__description">{description}</p>

        {effets && (
          <ul className="mode__effets">
            {effets.map((effet) => <li key={effet}>{effet}</li>)}
          </ul>
        )}

        {note && <p className="mode__note">{note}</p>}

        {children}
      </div>

      {/* Le même interrupteur que celui de la détection vocale : mêmes classes,
          même dessin. Un second modèle pour la même action rendrait
          l'application plus difficile à lire qu'à écrire. */}
      <button
        type="button"
        className={`bascule ${actif ? 'bascule--active' : ''}`}
        onClick={onBasculer}
        disabled={occupe || !connu}
        role="switch"
        aria-checked={actif}
        aria-label={titre}
      >
        <span className="bascule__piste">
          <span className="bascule__bouton" />
        </span>
      </button>
    </div>
  );
}

/**
 * La synchronisation du catalogue vers Stripe.
 *
 * POURQUOI UN BOUTON ET NON UN APPEL AU DÉMARRAGE
 * ----------------------------------------------
 * Créer des tarifs chez un prestataire de paiement est une écriture, pas une
 * lecture. Elle doit être voulue, et datée.
 *
 * QUAND S'EN SERVIR
 * -----------------
 * Une fois au passage en production — les identifiants de tarifs créés dans le
 * compte de test n'existent pas dans le compte réel, et un paiement les
 * réclamerait en vain. Puis à chaque changement de prix.
 *
 * Idempotente : un tarif déjà au bon montant est réutilisé. Chez Stripe un
 * tarif ne se modifie pas — c'est voulu, les abonnements en cours continuent
 * sur l'ancien montant — donc changer un prix crée un NOUVEAU tarif, et seuls
 * les nouveaux abonnés le paient.
 */
function CatalogueStripe() {
  const [envoi, setEnvoi] = useState(false);
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState(null);

  const lancer = async () => {
    setEnvoi(true);
    setErreur(null);
    setLignes(null);

    try {
      const { data } = await synchroniserCatalogueStripe();
      setLignes(data.lignes ?? []);
    } catch (e) {
      setErreur(
        e?.response?.data?.message
        ?? "Le catalogue n'a pas pu être synchronisé.",
      );
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="catalogue-stripe">
      <h3 className="catalogue-stripe__titre">Catalogue Stripe</h3>

      <p className="catalogue-stripe__note">
        Crée chez Stripe les produits et tarifs de vos formules et recharges, et
        enregistre leurs identifiants. À lancer au passage en production, puis à
        chaque changement de prix. Relancer ne crée pas de doublon.
      </p>

      {erreur && <div className="alert">{erreur}</div>}

      <button type="button" className="btn btn--compact" disabled={envoi} onClick={lancer}>
        {envoi ? 'Synchronisation…' : 'Synchroniser le catalogue'}
      </button>

      {/* Le détail ligne par ligne, et non un simple « c'est fait » : c'est la
          seule occasion de voir ce qui a été créé chez un prestataire de
          paiement, et de repérer une formule oubliée. */}
      {lignes && (
        <ul className="catalogue-stripe__lignes">
          {lignes.map((l) => (
            <li key={l.code}>
              <strong>{l.libelle}</strong> — {l.etat}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * La place occupée par la base.
 *
 * POURQUOI CE BLOC EXISTE
 * -----------------------
 * La production tourne sur SQL Server Express : dix gigaoctets, et une base
 * pleine n'accepte plus une seule écriture — ce n'est pas une lenteur, c'est
 * l'application qui s'arrête. Le chiffre ne s'obtenait qu'en ouvrant un client
 * SQL sur la production, autant dire jamais.
 *
 * On affiche la part des DOCUMENTS à part, parce que c'est le seul poste qu'on
 * pilote : il vaut « dépôts par jour × jours de rétention », et la rétention
 * est une valeur de configuration. Le reste croît avec l'usage.
 */
function PlaceBase() {
  const [base, setBase] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;

    getEtatBase()
      .then(({ data }) => { if (vivant) setBase(data); })
      .catch(() => {
        if (vivant) setErreur("La place occupée n'a pas pu être lue.");
      });

    return () => { vivant = false; };
  }, []);

  if (erreur) return <div className="alert">{erreur}</div>;
  if (!base) return null;

  // ZÉRO MÉGAOCTET N'EXISTE PAS, DONC ON NE L'AFFICHE PAS.
  //
  // `sys.database_files` n'est lisible que si le compte de l'application y a
  // droit ; sinon la requête rend zéro ligne, sans erreur. Afficher « 0 Mo »
  // ferait conclure à une base vide et rassurerait à tort — c'est la même
  // règle que « État inconnu » sur les interrupteurs plus haut.
  const lu = base.donneesMo > 0;

  const plafond = base.plafondMo ?? null;
  const part = plafond ? Math.min(100, Math.round((base.donneesMo / plafond) * 100)) : null;

  // Les mêmes trois seuils que la consommation des familles : sous 50 %, il n'y
  // a rien à dire, et une échelle continue serait jolie sans rien signifier.
  const teinte = part === null || part < 50 ? 'normal' : part < 80 ? 'proche' : 'plein';

  return (
    <div className="place-base">
      <h3 className="place-base__titre">Place occupée</h3>

      {!lu && (
        <p className="place-base__note">
          La taille des fichiers n’a pas pu être lue — le compte SQL de
          l’application n’a probablement pas accès à <code>sys.database_files</code>.
          Le détail des documents ci-dessous reste exact.
        </p>
      )}

      {lu && (plafond ? (
        <div className="conso place-base__jauge">
          <span className="conso__jauge">
            <span
              className={`conso__part conso__part--${teinte}`}
              style={{ width: `${Math.max(part, 1)}%` }}
            />
          </span>
          <span className="conso__chiffre">{part} %</span>
        </div>
      ) : (
        <p className="place-base__note">
          Édition sans plafond de taille — il n’y a pas de jauge à afficher.
        </p>
      ))}

      <ul className="place-base__lignes">
        {lu && (
          <li>
            <span>Données occupées</span>
            <strong>
              {base.donneesMo} Mo{plafond ? ` / ${Math.round(plafond / 1024)} Go` : ''}
            </strong>
          </li>
        )}
        <li>
          <span>dont documents ({base.documentsNombre})</span>
          <strong>{base.documentsMo} Mo</strong>
        </li>

        {/* HORS PLAFOND, ET C'EST DIT. Le journal occupe du disque, souvent
            plusieurs fois les données, mais Express ne borne que le fichier de
            données. L'additionner ferait paniquer pour rien. */}
        {lu && (
          <li className="place-base__hors">
            <span>Journal des transactions</span>
            <strong>{base.journalMo} Mo</strong>
          </li>
        )}
      </ul>

      <p className="place-base__note">
        {base.edition}. Le poste qu’on pilote est celui des documents : il vaut
        « dépôts par jour × jours de rétention », et la rétention est de trois
        jours. Sous 50 %, il n’y a rien à faire.
      </p>
    </div>
  );
}

/** Une file et son contenu, repliée par défaut. */
function File({ titre, cout, planches }) {
  const [ouverte, setOuverte] = useState(false);
  const vide = planches.length === 0;

  return (
    <div className={`file ${vide ? 'file--vide' : ''}`}>
      <button
        type="button"
        className="file__entete"
        onClick={() => setOuverte((o) => !o)}
        disabled={vide}
        aria-expanded={ouverte}
      >
        <span className="file__nombre">{planches.length}</span>

        <span className="file__texte">
          <strong className="file__titre">{titre}</strong>
          <span className="file__cout">{cout}</span>
        </span>

        {!vide && <span className="file__chevron">{ouverte ? '▾' : '▸'}</span>}
      </button>

      {/* LA PLUS RÉCENTE EN HAUT, comme le worker les prend. La liste doit se
          lire comme l'ordre de traitement, sinon elle ne dit pas ce qui va
          être payé en premier. */}
      {ouverte && (
        <ul className="file__lignes">
          {planches.map((p) => (
            <li key={p.cle}>
              <code>{p.cle}</code>
              <span className="file__matiere">{p.matiereCode}</span>
              <span className="file__poids">{Math.round(p.taille / 1024)} Ko</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Ce qui attend d'être traité par le worker des planches.
 *
 * POURQUOI CET ÉCRAN EXISTE
 * -------------------------
 * Le journal des appels dit ce qui a été dépensé ; il ne dit jamais ce qu'il
 * RESTE à dépenser. La nuit où la facture est montée, il a fallu interroger la
 * base à la main pour savoir combien de planches attendaient derrière.
 *
 * Trois files et non un total : décrire coûte une lecture d'image, cartographier
 * en coûte une seconde, créditer interroge une base extérieure. Un chiffre
 * unique cacherait laquelle s'emballe.
 */
function FilesPlanches() {
  const [files, setFiles] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [vidage, setVidage] = useState(false);
  const [confirme, setConfirme] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);

    try {
      const { data } = await getFilesPlanches();
      setFiles({
        aDecrire: data?.aDecrire ?? [],
        aCartographier: data?.aCartographier ?? [],
        aCrediter: data?.aCrediter ?? [],
      });
    } catch {
      setErreur("Les files n'ont pas pu être lues.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const vider = async () => {
    setVidage(true);
    setErreur(null);

    try {
      await viderFilesPlanches();
      setConfirme(false);
      await charger();
    } catch {
      setErreur("Les files n'ont pas pu être vidées.");
    } finally {
      setVidage(false);
    }
  };

  const total = files
    ? files.aDecrire.length + files.aCartographier.length + files.aCrediter.length
    : 0;

  return (
    <div className="files-planches">
      <h3 className="files-planches__titre">Files des planches</h3>

      <p className="files-planches__note">
        Ce que le worker des planches traitera au prochain import. Il ne tourne
        pas au chronomètre : il dort jusqu’à ce qu’une planche soit importée,
        vide ses files, et se rendort. La plus récente d’abord.
      </p>

      {erreur && <div className="alert">{erreur}</div>}

      {chargement && !files ? (
        <Loader texte="Lecture des files…" />
      ) : files && (
        <>
          <div className="files-planches__liste">
            <File
              titre="À décrire"
              cout="Une lecture d’image par planche"
              planches={files.aDecrire}
            />
            <File
              titre="À cartographier"
              cout="Une seconde lecture, pour placer les repères"
              planches={files.aCartographier}
            />
            <File
              titre="À créditer"
              cout="Recherche de l’origine, sans lecture d’image"
              planches={files.aCrediter}
            />
          </div>

          <div className="files-planches__actions">
            <button
              type="button"
              className="btn btn--compact btn--fantome"
              onClick={charger}
              disabled={chargement || vidage}
            >
              {chargement ? 'Lecture…' : 'Rafraîchir'}
            </button>

            {/* DEUX CLICS, PAS UN. Vider marque définitivement des dizaines de
                planches comme non traitées : c'est réversible en les
                réimportant, mais ça ne doit pas partir d'un clic distrait. */}
            {total > 0 && (confirme ? (
              <>
                <button
                  type="button"
                  className="btn btn--compact btn--danger"
                  onClick={vider}
                  disabled={vidage}
                >
                  {vidage ? 'Vidage…' : `Confirmer : vider ${total} attente(s)`}
                </button>

                <button
                  type="button"
                  className="btn btn--compact btn--fantome"
                  onClick={() => setConfirme(false)}
                  disabled={vidage}
                >
                  Annuler
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn--compact btn--fantome"
                onClick={() => setConfirme(true)}
              >
                Vider les files
              </button>
            ))}
          </div>

          <p className="files-planches__avertissement">
            Vider n’appelle PAS le modèle et ne supprime aucune planche : chaque
            attente est marquée traitée, avec un contenu qui dit qu’elle ne l’a
            pas été. Les planches concernées s’afficheront toujours au tableau,
            mais sans repères cliquables. Réimporter une planche la remet dans
            la file.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * L'OFFRE DE LANCEMENT : UN INTERRUPTEUR ET DEUX RÉGLAGES.
 *
 * C'est le seul mode qui ne se résume pas à un booléen — il a un habillage,
 * et cet habillage doit se préparer À FROID. On écrit le texte et l'échéance
 * un vendredi, on allume le lundi. D'où deux champs sous l'interrupteur
 * plutôt qu'un écran séparé : ce qui se règle ensemble se montre ensemble.
 *
 * LE BOUTON D'ENREGISTREMENT NE CONCERNE QUE LES DEUX CHAMPS. L'interrupteur,
 * lui, part immédiatement — c'est le geste d'urgence, celui qu'on fait pour
 * arrêter une promotion qui dérape, et il ne doit pas dépendre de l'état d'un
 * formulaire à côté.
 */
function OffreLancement({
  reglages, connu, occupe, occupeBandeau, onBasculer, onBasculerBandeau, onEnregistrer,
}) {
  const [texte, setTexte] = useState(reglages.offreLancementTexte);
  const [fin, setFin] = useState(() => pourLeChamp(reglages.offreLancementFin));
  // EN HEURES DANS LE CHAMP, EN MINUTES SUR LE FIL. L'administrateur pense
  // en heures ; le reste du produit compte en minutes. La conversion vit
  // ici, à l'unique endroit où les deux se rencontrent.
  const [heures, setHeures] = useState(() => reglages.offreLancementMinutes / 60);

  // UN TABLEAU DANS L'ÉCRAN, UNE CHAÎNE SUR LE FIL. Les cases à cocher
  // manipulent une liste ; le réglage est une chaîne séparée par des
  // virgules. La conversion vit ici, au seul endroit où les deux formes se
  // rencontrent.
  const [formules, setFormules] = useState(() => enListe(reglages.offreLancementFormules));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Les valeurs arrivent APRÈS le premier rendu — la lecture est
  // asynchrone. Sans cette resynchronisation, les champs resteraient vides
  // sur ce qui est pourtant enregistré, et le premier enregistrement
  // effacerait le réglage en place.
  useEffect(() => {
    setTexte(reglages.offreLancementTexte);
    setFin(pourLeChamp(reglages.offreLancementFin));
    setHeures(reglages.offreLancementMinutes / 60);
    setFormules(enListe(reglages.offreLancementFormules));
  }, [
    reglages.offreLancementTexte,
    reglages.offreLancementFin,
    reglages.offreLancementMinutes,
    reglages.offreLancementFormules,
  ]);

  const echeance = fin ? new Date(fin) : null;
  const passee = echeance !== null && !Number.isNaN(echeance.getTime())
    && echeance.getTime() <= Date.now();

  const enregistrer = async () => {
    setEnvoi(true);
    setErreur(null);

    try {
      // `toISOString()` PORTE LE FUSEAU. La valeur d'un `datetime-local` est
      // une heure murale sans fuseau : l'envoyer telle quelle laisserait le
      // serveur la lire comme de l’UTC, et la promotion finirait deux heures
      // trop tôt en été.
      await onEnregistrer(
        texte.trim(),
        echeance && !Number.isNaN(echeance.getTime()) ? echeance.toISOString() : '',
        Math.round(Number(heures) * 60),
        formules,
      );
    } catch (e) {
      setErreur(
        e?.response?.data?.message ?? "L’offre n’a pas pu être enregistrée.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="mode mode--reglable">
      <div className="mode__texte">
        <strong className="mode__titre">
          Offre de lancement

          <span className={`mode__etat ${connu && reglages.offreLancement ? 'mode__etat--actif' : ''}`}>
            {!connu ? 'État inconnu' : reglages.offreLancement ? 'Activée' : 'Désactivée'}
          </span>
        </strong>

        <p className="mode__description">
          Les formules cochées affichent leur forfait augmenté des heures offertes, le
          premier mois, et ces heures sont créditées automatiquement à la
          souscription. Le crédit est
          géré chez nous, pas chez Stripe : éteindre l’interrupteur suffit à
          arrêter les suivantes, et personne ne garde de remise à vie.
        </p>

        <ul className="mode__effets">
          <li>Les cartes concernées portent la mention entre parenthèses, et leur ligne de forfait est barrée.</li>
          <li>Un compte à rebours apparaît sur la page d’accueil, si une date de fin est réglée.</li>
          <li>Les heures sont créditées après ENCAISSEMENT, jamais à l’ouverture de la page de paiement.</li>
          <li>Mensuel uniquement : « le premier mois » n’a pas de sens sur un abonnement annuel.</li>
          <li>Les heures déjà offertes restent acquises quand vous éteignez l’interrupteur.</li>
        </ul>

        {erreur && <div className="alert">{erreur}</div>}

        {/* CE QU’ON OFFRE VIENT AVANT COMMENT ON L’HABILLE. La mention et
            l’échéance sont de la mise en scène ; ce champ-ci décide de ce
            que le parent reçoit réellement. */}
        {/* QUI AVANT COMBIEN. La formule concernée est la première décision :
            offrir trois heures sur Solo et sur Famille ne coûte pas la même
            chose, et ne vise pas les mêmes gens. */}
        <div className="champ">
          <span className="champ__intitule">Formules concernées</span>

          <div className="mode__cases">
            {reglages.formulesDisponibles.map((o) => (
              <label key={o.code} className="mode__case">
                <input
                  type="checkbox"
                  checked={formules.includes(o.code)}
                  onChange={() => setFormules((liste) => (
                    liste.includes(o.code)
                      ? liste.filter((f) => f !== o.code)
                      : [...liste, o.code]
                  ))}
                />
                {o.libelle}
              </label>
            ))}
          </div>

          <span className="champ__aide">
            {/* AUCUNE COCHÉE ÉTEINT L’OFFRE, et c’est dit : sinon on
                chercherait pourquoi la carte ne change pas alors que
                l’interrupteur est allumé. */}
            {formules.length === 0
              ? 'Aucune formule cochée : l’offre ne s’appliquera à rien.'
              : 'Les cartes cochées porteront la mention et les heures offertes. '
                + 'Les abonnements annuels ne sont jamais concernés.'}
          </span>
        </div>

        <div className="champ">
          <label htmlFor="lancement-heures">Heures offertes</label>
          <input
            id="lancement-heures"
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={heures}
            onChange={(e) => setHeures(e.target.value)}
          />
          <span className="champ__aide">
            {/* AUCUN RAPPORT AVEC LES PACKS VENDUS. Ce sont des heures
                offertes, créditées chez nous — rien à créer chez Stripe, et
                aucun produit marchand à inventer pour faire un cadeau. */}
            Créditées automatiquement à la souscription, sur la période en
            cours. Rien à créer chez Stripe : le cadeau se gère entièrement
            ici. À zéro, l’offre ne donne plus rien et la mention disparaît.
          </span>
        </div>

        <div className="mode__champs">
          <div className="champ">
            <label htmlFor="lancement-texte">Mention affichée à côté de « Solo »</label>
            <input
              id="lancement-texte"
              maxLength={40}
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="OFFRE LANCEMENT"
            />
            <span className="champ__aide">
              S’affiche entre parenthèses : ({texte.trim() || "OFFRE LANCEMENT"})
            </span>
          </div>

          <div className="champ">
            <label htmlFor="lancement-fin">Date et heure de fin</label>
            <input
              id="lancement-fin"
              type="datetime-local"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
            />
            <span className="champ__aide">
              {/* CONTRAIGNANTE, PAS DÉCORATIVE. Annoncer une échéance et ne
                  pas l’appliquer laisserait un visiteur souscrire après la
                  fin en croyant recevoir le cadeau. */}
              Laissée vide, l’offre court sans terme et aucun compte à rebours
              n’apparaît. Passée cette date, l’offre s’éteint d’elle-même —
              même si l’interrupteur est resté allumé.
            </span>
          </div>
        </div>

        {/* UN SECOND INTERRUPTEUR, PLUS PETIT, PARCE QUE C’EST UNE DÉCISION
            PLUS PETITE. L’offre est un engagement commercial : elle change
            la carte Solo et crédite des heures. Le compte à rebours n’est
            qu’une vitrine — il presse le visiteur, et on peut vouloir de
            l’un sans l’autre.

            Le cas courant : les premiers jours, « il reste 26 jours » ne
            presse personne. On allume l’offre tout de suite et le décompte
            la dernière semaine, quand il a enfin quelque chose à dire. */}
        <div className="mode__sous-reglage">
          <div className="mode__sous-texte">
            <strong>Compte à rebours sur la page d’accueil</strong>
            <span>
              {reglages.offreLancementBandeau
                ? 'Le bandeau « Fin de l’offre dans… » est affiché en haut de la page d’accueil.'
                : 'L’offre reste active, mais aucun décompte n’est affiché aux visiteurs.'}
            </span>

            {/* SANS DATE, PAS DE DÉCOMPTE — quel que soit cet interrupteur.
                Le dire ici évite de chercher pourquoi le bandeau reste
                invisible alors qu’il est allumé. */}
            {!fin && (
              <span className="mode__sous-note">
                Réglez une date de fin ci-dessus : sans elle, il n’y a rien à
                décompter et le bandeau ne s’affiche pas.
              </span>
            )}
          </div>

          <button
            type="button"
            className={`bascule bascule--mini ${reglages.offreLancementBandeau ? 'bascule--active' : ''}`}
            onClick={onBasculerBandeau}
            disabled={occupeBandeau || !connu}
            role="switch"
            aria-checked={reglages.offreLancementBandeau}
            aria-label="Compte à rebours sur la page d’accueil"
          >
            <span className="bascule__piste">
              <span className="bascule__bouton" />
            </span>
          </button>
        </div>

        {/* LE CAS QU’IL FAUT LE PLUS SIGNALER : l’interrupteur est allumé et
            l’administrateur croit son offre en cours, alors qu’elle est morte
            depuis mardi soir. */}
        {passee && reglages.offreLancement && (
          <p className="mode__alerte">
            La date de fin est dépassée : l’offre n’est plus appliquée, malgré
            l’interrupteur allumé.
          </p>
        )}

        <div className="mode__actions">
          <button
            type="button"
            className="btn btn--compact btn--fantome"
            disabled={envoi || !connu}
            onClick={enregistrer}
          >
            {envoi ? "Enregistrement…" : "Enregistrer les réglages de l’offre"}
          </button>
        </div>
      </div>

      <button
        type="button"
        className={`bascule ${reglages.offreLancement ? 'bascule--active' : ''}`}
        onClick={onBasculer}
        disabled={occupe || !connu}
        role="switch"
        aria-checked={reglages.offreLancement}
        aria-label="Offre de lancement"
      >
        <span className="bascule__piste">
          <span className="bascule__bouton" />
        </span>
      </button>
    </div>
  );
}

/**
 * L'échéance enregistrée, mise à la forme d'un champ `datetime-local`.
 *
 * Le champ n'accepte QUE « AAAA-MM-JJTHH:MM », en heure locale et sans
 * fuseau. La valeur stockée, elle, est un ISO complet en UTC : la donner
 * telle quelle laisserait le champ vide, sans erreur, et l'administrateur
 * croirait n’avoir jamais réglé de date.
 */
/**
 * La chaîne du réglage, découpée en liste de codes.
 *
 * `SOLO,DUO` devient `['SOLO', 'DUO']`. Une chaîne vide donne une liste
 * vide — et c'est une décision, pas une absence : l'administrateur a
 * décoché toutes les formules.
 */
function enListe(chaine) {
  return (chaine ?? '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

function pourLeChamp(iso) {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const deux = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${deux(date.getMonth() + 1)}-${deux(date.getDate())}`
       + `T${deux(date.getHours())}:${deux(date.getMinutes())}`;
}

/**
 * LES TROIS CYCLES DES JEUX — Camara, le 20/09/2026.
 *
 * Les classes sont écrites en toutes lettres : « les collégiens » ne dit pas,
 * à quelqu'un qui découvre l'écran, si la 6e en fait partie.
 */
const CYCLES_JEUX = [
  { cle: 'JEUX_PRIMAIRE', champ: 'jeuxPrimaire', titre: 'Primaire', classes: 'élèves du CP au CM2' },
  { cle: 'JEUX_COLLEGE', champ: 'jeuxCollege', titre: 'Collège', classes: 'collégiens, de la 6e à la 3e' },
  { cle: 'JEUX_LYCEE', champ: 'jeuxLycee', titre: 'Lycée', classes: 'lycéens, de la 2de à la terminale' },
];

export default function Modes() {
  const [reglages, setReglages] = useState({
    modeTest: false,
    compteTest: true,
    essaisOuverts: true,
    tachesDeFond: true,
    maintenance: false,
    voixDeSecours: false,
    voixGemini: false,
    voixGeminiDisponible: false,
    voixGeminiModele: '',
    blueSky: false,
    styleSaison: null,
    modeDeveloppeur: false,

    // Les trois portes des jeux, éteintes par défaut : aucun jeu n existe.
    jeuxPrimaire: false,
    jeuxCollege: false,
    jeuxLycee: false,

    // Le coupe-circuit du temps réel, allumé par défaut comme sur le serveur.
    fluxSse: true,

    // La salle d'attente : éteinte, et son plafond de places.
    affluence: false,
    affluencePlaces: 300,

    // L'offre de lancement : trois valeurs qui n'ont de sens qu'ensemble.
    offreLancement: false,
    offreLancementTexte: '',
    offreLancementFin: '',
    offreLancementBandeau: true,
    offreLancementMinutes: 180,
    offreLancementFormules: 'SOLO',

    // Le catalogue, pour cocher des formules réelles plutôt que de faire
    // taper des codes qui n'existent peut-être pas.
    formulesDisponibles: [],

    // Les langues qu'on peut montrer ou cacher — celles qui ont une matière
    // en base. Vide tant que le serveur n'a pas répondu.
    langues: [],
  });
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(null);
  const [erreur, setErreur] = useState(null);

  // Les réglages ont-ils été LUS ? Distinct de « chargement terminé » : après
  // un échec, le chargement est fini mais on ne sait rien.
  const [lus, setLus] = useState(false);

  useEffect(() => {
    let vivant = true;

    getReglages()
      .then(({ data }) => {
        if (!vivant) return;

        setReglages({
          modeTest: Boolean(data?.modeTest),
          // Actif par défaut : le compte existe pour servir, et un
          // administrateur qui n'y a jamais touché s'attend à ce qu'il marche.
          compteTest: data?.compteTest !== false,

          // Ouverts par défaut, pour la même raison : ne jamais fermer une
          // porte parce qu'un réglage n'a pas encore été posé.
          essaisOuverts: data?.essaisOuverts !== false,
          tachesDeFond: data?.tachesDeFond !== false,

          // Éteinte par défaut : une lecture qui échoue ne doit jamais
          // conclure que le site est fermé.
          maintenance: Boolean(data?.maintenance),

          // Éteint par défaut : le modèle principal reste le meilleur des
          // deux quand il fonctionne.
          voixDeSecours: Boolean(data?.voixDeSecours),

          // Ce que dit le serveur, qui tient compte de la configuration quand
          // rien n'a encore été enregistré — et s'il a seulement une clé.
          voixGemini: Boolean(data?.voixGemini),
          voixGeminiDisponible: Boolean(data?.voixGeminiDisponible),
          voixGeminiModele: data?.voixGeminiModele ?? '',

          // Éteint par défaut : le style d'origine reste la référence.
          blueSky: Boolean(data?.blueSky),
          styleSaison: data?.styleSaison ?? null,

          // Éteint par défaut, et il doit le rester hors essais : allumé, il
          // vaut pour toutes les séances, celles des vrais élèves comprises.
          modeDeveloppeur: Boolean(data?.modeDeveloppeur),

          jeuxPrimaire: Boolean(data?.jeuxPrimaire),
          jeuxCollege: Boolean(data?.jeuxCollege),
          jeuxLycee: Boolean(data?.jeuxLycee),

          // Allumé par défaut : `!== false` et non `Boolean(...)`, pour qu'une
          // API antérieure à ce drapeau n'affiche pas le temps réel comme
          // éteint alors qu'il fonctionne.
          fluxSse: data?.fluxSse !== false,
          affluence: Boolean(data?.affluence),
          affluencePlaces: Number(data?.affluencePlaces) || 300,

          offreLancement: Boolean(data?.offreLancement),
          offreLancementTexte: data?.offreLancementTexte ?? '',
          offreLancementFin: data?.offreLancementFin ?? '',

          // Allumé par défaut : c'était le comportement avant que ce
          // réglage existe.
          offreLancementBandeau: data?.offreLancementBandeau !== false,
          // Absent = jamais réglé : on retombe sur le défaut du serveur,
          // trois heures, plutôt que sur zéro qui éteindrait le cadeau.
          offreLancementMinutes:
            Number(data?.offreLancementMinutes) || 180,

          // `?? 'SOLO'` et non `|| 'SOLO'` : une chaîne VIDE est une
          // décision — toutes les formules ont été décochées — quand
          // l’absence de clé veut dire « jamais réglé ». Les confondre
          // recocherait Solo à chaque chargement.
          offreLancementFormules: data?.offreLancementFormules ?? 'SOLO',

          formulesDisponibles: Array.isArray(data?.formulesDisponibles)
            ? data.formulesDisponibles
            : [],

          langues: Array.isArray(data?.langues) ? data.langues : [],
        });

        setLus(true);
      })
      .catch(() => {
        if (vivant) setErreur("Les réglages n'ont pas pu être chargés.");
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });

    return () => { vivant = false; };
  }, []);

  /**
   * Enregistre le plafond de la salle d'attente.
   *
   * IL S'ENREGISTRE MÊME SALLE ÉTEINTE, et c'est tout l'intérêt : on règle le
   * nombre à froid, un jour calme, pour n'avoir qu'un interrupteur à trouver
   * le jour où le serveur souffre.
   */
  const enregistrerPlaces = async (places) => {
    setEnvoi('AFFLUENCE_PLACES');
    setErreur('');

    try {
      await definirPlacesAffluence(places);
      setReglages((etat) => ({ ...etat, affluencePlaces: places }));
    } catch {
      setErreur("Le nombre de places n'a pas pu être enregistré.");
    } finally {
      setEnvoi(null);
    }
  };

  const basculer = async (cle, champ) => {
    const nouveau = !reglages[champ];

    setEnvoi(cle);
    setErreur(null);

    try {
      await definirReglage(cle, nouveau);
      setReglages((etat) => ({ ...etat, [champ]: nouveau }));

      // Les drapeaux publics sont retenus le temps d'une session : sans cet
      // oubli, la barre de navigation et l'accueil garderaient l'ancien état
      // jusqu'au rechargement. Vrai pour les deux réglages publics, pas
      // seulement le mode test — l'essai en fait partie depuis qu'il pilote
      // le bouton de l'accueil.
      // `FLUX_SSE` en fait partie, et pas seulement pour l'affichage : c'est
      // cette relecture qui referme ou rouvre l'écoute de VOTRE onglet. Sans
      // elle, l'administrateur qui éteint le temps réel garderait la seule
      // connexion ouverte du site — celle qu'il vient de couper aux autres.
      if (cle === 'MODE_TEST' || cle === 'ESSAIS_OUVERTS' || cle === 'MAINTENANCE_ACTIVE'
          || cle === 'OFFRE_LANCEMENT' || cle === 'OFFRE_LANCEMENT_BANDEAU'
          || cle === 'BLUE_SKY' || cle === 'FLUX_SSE'
          || cle.startsWith('JEUX_')) {
        oublierReglages();
      }
    } catch {
      setErreur("Le réglage n'a pas pu être enregistré.");
    } finally {
      setEnvoi(null);
    }
  };

  /**
   * MONTRER OU CACHER UNE LANGUE — Camara, le 04/10/2026. L'équipe de la page
   * d'accueil et la liste des classes sont lues ailleurs et gardées en mémoire :
   * le changement se voit au prochain chargement de ces écrans.
   */
  const basculerLangue = async (code) => {
    const langue = reglages.langues.find((l) => l.code === code);
    if (!langue) return;

    const cle = `LANGUE_${code}`;
    setEnvoi(cle);
    setErreur(null);

    try {
      await definirLangue(code, !langue.actif);
      setReglages((etat) => ({
        ...etat,
        langues: etat.langues.map((l) => (l.code === code ? { ...l, actif: !l.actif } : l)),
      }));
    } catch {
      setErreur("La langue n'a pas pu être enregistrée.");
    } finally {
      setEnvoi(null);
    }
  };

  /**
   * LA DÉCLINAISON DE BLUE SKY — Camara, le 29/09/2026. Une seule à la fois ;
   * `null` revient au Blue Sky classique. En choisir une allume Blue Sky
   * (le serveur le fait), d'où la mise à jour des deux champs.
   */
  const choisirSaison = async (saison) => {
    setEnvoi('STYLE_SAISON');
    setErreur(null);

    try {
      await definirStyleSaison(saison);
      setReglages((etat) => ({
        ...etat,
        styleSaison: saison,
        blueSky: saison ? true : etat.blueSky,
      }));

      // Même raison que pour Blue Sky : les drapeaux publics sont retenus le
      // temps d'une session, et c'est leur relecture qui repeint cet onglet.
      oublierReglages();
    } catch {
      setErreur("La déclinaison n'a pas pu être enregistrée.");
    } finally {
      setEnvoi(null);
    }
  };

  if (chargement) return <Loader texte="Chargement des réglages…" />;

  return (
    <div className="bloc-profil">
      {erreur && <div className="alert">{erreur}</div>}

      <div className="modes">
        {/* EN PREMIER, ET CE N'EST PAS DE LA MISE EN PAGE. C'est le seul
            interrupteur qui remplace le site entier par une autre page : le
            ranger au milieu des autres le ferait basculer par erreur, et le
            chercher sous la pression le ferait manquer. */}
        {/* JUSTE APRÈS LA MAINTENANCE, ET POUR LA MÊME RAISON : c’est un
            interrupteur qu’on cherche sous la pression, quand quelque chose
            ne va pas. Il doit être en haut, pas au milieu des réglages de
            confort. */}
        {/* EN TÊTE DES RÉGLAGES DE CONFORT, sous les deux interrupteurs
            d’urgence : une promotion se règle posément, mais elle se coupe
            parfois vite — un prix mal annoncé se corrige en minutes, pas en
            heures. */}
        <OffreLancement
          reglages={reglages}
          connu={lus}
          occupe={envoi === 'OFFRE_LANCEMENT'}
          occupeBandeau={envoi === 'OFFRE_LANCEMENT_BANDEAU'}
          onBasculer={() => basculer('OFFRE_LANCEMENT', 'offreLancement')}
          onBasculerBandeau={() =>
            basculer('OFFRE_LANCEMENT_BANDEAU', 'offreLancementBandeau')}
          onEnregistrer={async (texte, fin, minutes, formules) => {
            // LES QUATRE VALEURS, ET PAS DEUX.
            //
            // Ce gestionnaire n’en recevait que deux alors que le formulaire
            // en envoie quatre : les heures et les formules étaient jetées
            // ici, silencieusement, avant même de partir. Le serveur écrivait
            // alors zéro heure et aucune formule, et l’offre s’éteignait toute
            // seule pendant que l’écran continuait d’afficher ses valeurs par
            // défaut.
            //
            // JavaScript ne dit rien quand on ignore des arguments. C’est
            // exactement le genre de faute qu’aucun test d’écran ne rattrape,
            // et qu’on ne voit qu’en regardant la base.
            await definirOffreLancement(texte, fin, minutes, formules);

            setReglages((etat) => ({
              ...etat,
              offreLancementTexte: texte,
              offreLancementFin: fin,
              offreLancementMinutes: minutes,

              // Rangée sous la forme que la lecture rend : une chaîne séparée
              // par des virgules. Y laisser le tableau ferait diverger l’état
              // local de ce que renverra le prochain chargement.
              offreLancementFormules: formules.join(','),
            }));

            // Les drapeaux publics sont retenus le temps d’une session : sans
            // cet oubli, la page des tarifs garderait l’ancienne mention
            // jusqu’au rechargement.
            oublierReglages();
          }}
        />

        {/* LE CHOIX DE LA VOIX — Camara, le 28/09/2026 : « je veux garder
            Google Flash-Lite, mais garder dans l'admin l'option GPT actuelle
            afin de pouvoir basculer ». Juste avant le secours : c'est le
            réglage de tous les jours, le secours celui des mauvais jours. */}
        <Interrupteur
          titre="Voix des professeurs — Gemini Flash-Lite"
          actif={reglages.voixGemini && reglages.voixGeminiDisponible}
          connu={lus && reglages.voixGeminiDisponible}
          occupe={envoi === 'VOIX_GEMINI'}
          onBasculer={() => basculer('VOIX_GEMINI', 'voixGemini')}
          description={
            !reglages.voixGeminiDisponible
              ? "Aucune clé Gemini n’est configurée sur ce serveur : les professeurs parlent avec GPT-4o-mini, et cet interrupteur ne peut rien allumer. La clé se pose dans Voix:GeminiApiKey (appsettings.Production.json), puis l’API se redémarre."
              : `En ce moment, le professeur parle avec ${reglages.voixDeSecours ? 'TTS-1 (la voix de secours a la priorité)' : reglages.voixGemini ? `Gemini (${reglages.voixGeminiModele})` : 'GPT-4o-mini'}. Allumé, les professeurs parlent avec la voix de Google ; éteint, ils reprennent celle d’OpenAI, comme avant le 28 septembre 2026.`
          }
          effets={[
            'Le changement est immédiat, y compris pour les cours déjà commencés — le choix est relu à chaque phrase.',
            'Chaque professeur a sa voix dans les deux cas, mais ce ne sont pas les mêmes timbres : un élève entendra la différence.',
            'Si Google ne répond pas, la phrase repart aussitôt chez OpenAI : le professeur ne reste jamais muet.',
            'La transcription du micro et les voix pré-enregistrées des jeux ne changent pas : elles restent chez OpenAI.',
          ]}
          note="MESURÉ LE 28/09/2026. Au banc, Gemini Flash-Lite donnait son premier son en 0,55 à 0,90 s à chaque appel ; GPT-4o-mini avait une médiane semblable mais des pics à 24 et 32 secondes — les longs silences entendus en séance. Coût comparable aujourd’hui (≈ 1,4 contre 1,5 centime la minute), à revérifier en janvier 2027."
        />

        <Interrupteur
          titre="Voix de secours — TTS-1"
          actif={reglages.voixDeSecours}
          connu={lus}
          occupe={envoi === 'VOIX_DE_SECOURS'}
          onBasculer={() => basculer('VOIX_DE_SECOURS', 'voixDeSecours')}
          description={`En ce moment, le professeur parle avec ${reglages.voixDeSecours ? "TTS-1" : reglages.voixGemini && reglages.voixGeminiDisponible ? "Gemini" : "GPT-4o-mini"}. Cet interrupteur bascule toute la synthèse vocale sur « tts-1 » quand le modèle principal se met à produire des clics — ce qui est arrivé le 3 septembre 2026, sans prévenir et sans qu’on puisse le corriger de notre côté.`}
          effets={[
            'Le professeur parle avec un modèle plus ancien, plus stable, et une voix choisie pour ce modèle-là — les timbres ne sont pas les mêmes que sur le modèle principal.',
            'La lenteur des dictées est conservée : elle passe par le réglage de vitesse au lieu d’une consigne au modèle.',
            'La prosodie est plus plate : le rythme ne varie plus à l’intérieur des phrases, et le ton ne s’adapte plus à l’âge de l’élève.',
            'Le changement est immédiat, y compris pour les cours déjà commencés — le mode est relu à chaque phrase.',
          ]}
          note="MESURÉ, PAS SUPPOSÉ. Sur la même phrase, le modèle principal produisait 248 ruptures franches par seconde dans la voix de Nora ; le secours en produit 8. Le défaut vient bien du fournisseur : le même texte demandé directement à son interface, sans passer par l’application, sort déjà abîmé. À laisser éteint tant que le modèle principal se tient — il sonne mieux."
        />

        <Interrupteur
          titre="Maintenance"
          actif={reglages.maintenance}
          connu={lus}
          occupe={envoi === 'MAINTENANCE_ACTIVE'}
          onBasculer={() => basculer('MAINTENANCE_ACTIVE', 'maintenance')}
          description="Le rideau. Les visiteurs voient une page d’attente soignée au lieu du site ; vous, connecté en administrateur, continuez de le parcourir normalement."
          effets={[
            'Tout le site est remplacé par la page d’attente, barre de navigation et pied de page compris.',
            'Vous passez au travers tant que votre session administrateur est ouverte.',
            'La page d’attente porte un lien « Accès administrateur » : c’est par là qu’on rentre depuis un autre appareil, ou après expiration de session.',
            'Les élèves déjà en cours voient la page d’attente à leur prochaine navigation.',
          ]}
          note="C’EST UN RIDEAU, PAS UN VERROU. L’API continue de répondre derrière — sans quoi vous ne pourriez plus rien faire, pas même relever le rideau. Pour fermer vraiment le service, il faut arrêter l’API : c’est un autre geste. Ne comptez donc pas là-dessus pour protéger quoi que ce soit."
        />

        {/* LE COUPE-CIRCUIT DU TEMPS RÉEL — voulu par Camara le 16/09/2026,
            avant le lancement : pouvoir écarter cette piste d'un clic si le
            serveur souffre. Haut dans la liste pour la même raison que la
            maintenance : c'est un interrupteur qu'on cherche sous la pression,
            pas un réglage de confort. */}
        <Interrupteur
          titre="Temps réel — propagation instantanée"
          actif={reglages.fluxSse}
          connu={lus}
          occupe={envoi === 'FLUX_SSE'}
          onBasculer={() => basculer('FLUX_SSE', 'fluxSse')}
          description="Chaque onglet ouvert garde une connexion d’écoute vers le serveur : un réglage basculé ici — Blue Sky, la maintenance, le bandeau — change sur tous les ordinateurs et téléphones en même temps, sans rechargement. Éteint, les réglages se propagent en une minute au lieu d’être instantanés."
          effets={[
            'Éteint, les écoutes en cours sont fermées immédiatement : le serveur ne garde plus une seule connexion ouverte.',
            'Rien ne cesse de fonctionner : les navigateurs continuent de relire les réglages chaque minute. Le site devient seulement moins prompt à changer d’allure.',
            'Rallumé, les onglets déjà ouverts reprennent le temps réel à leur prochaine relecture — pas besoin de faire recharger qui que ce soit.',
            'Une écoute au repos ne consomme ni calcul ni base de données : c’est une connexion silencieuse, douze octets toutes les vingt-cinq secondes.',
            'Le plafond est de 2 000 écoutes simultanées. Au-delà, les navigateurs sont refusés proprement et relisent périodiquement.',
          ]}
          note="LE COUPE-CIRCUIT DU LANCEMENT. À éteindre si le serveur souffre et que vous voulez écarter cette piste d’un clic : aucune fonction ne disparaît, seule l’instantanéité. L’état est relu au démarrage, il survit donc à un redémarrage du conteneur. Laissez-le allumé tant que rien ne va mal — c’est l’état normal du produit."
        />

        {/* LA SALLE D'ATTENTE — voulue par Camara le 25/09/2026. Juste après
            le coupe-circuit du temps réel, et pour la même raison : ce sont
            les deux interrupteurs qu'on cherche quand le serveur souffre. */}
        <Interrupteur
          titre="Salle d'attente — limiter le nombre de visiteurs"
          actif={reglages.affluence}
          connu={lus}
          occupe={envoi === 'AFFLUENCE_ACTIVE'}
          onBasculer={() => basculer('AFFLUENCE_ACTIVE', 'affluence')}
          description={`Le site n'admet plus que ${reglages.affluencePlaces} visiteurs à la fois. Les suivants voient une page qui leur donne leur rang dans la file, et entrent automatiquement à mesure que des places se libèrent.`}
          effets={[
            'Ceux qui sont déjà entrés ne sont pas dérangés : ils gardent leur place tant qu’ils naviguent.',
            'Une place se libère quand son visiteur ferme l’onglet, ou au bout d’une minute et demie sans activité.',
            'La file est servie dans l’ordre d’arrivée. Rafraîchir ne fait pas avancer, et ne fait pas non plus reculer.',
            'Vous, administrateur, passez toujours au travers — c’est ce qui vous permet d’éteindre.',
            'Les rappels de Stripe, les liens de désabonnement et le scan par téléphone ne sont jamais mis en file.',
            'L’éteindre vide la salle immédiatement : tout le monde entre.',
          ]}
          note="À N’ALLUMER QUE QUAND LE SITE SOUFFRE, et à éteindre dès qu’il respire. C’est un robinet, pas un réglage qu’on pose une fois pour toutes : allumé un jour ordinaire, il ferait patienter des parents que la machine aurait servis sans peine. Le bon nombre de places ne se devine pas — il se mesure par un test de charge, qui n’a pas encore eu lieu."
        />

        {/* LE PLAFOND, SÉPARÉ DE L'INTERRUPTEUR. On le règle à froid, avant
            d'en avoir besoin ; on allume dans l'urgence. Les mêler obligerait
            à choisir un chiffre au moment précis où l'on n'a pas le temps d'y
            réfléchir. */}
        <PlafondAffluence
          places={reglages.affluencePlaces}
          onEnregistrer={enregistrerPlaces}
          occupe={envoi === 'AFFLUENCE_PLACES'}
        />

        {/* LE STYLE DU SITE — voulu par Camara le 15/09/2026, pour faire
            tester le nouveau style et revenir à l'ancien quand il veut. */}
        <Interrupteur
          titre="Blue Sky — nouveau style du site"
          actif={reglages.blueSky}
          connu={lus}
          occupe={envoi === 'BLUE_SKY'}
          onBasculer={() => basculer('BLUE_SKY', 'blueSky')}
          description="Le style de la page de maintenance étendu à tout le site : le fond bleu de marque, des cartes en verre dépoli et les accents corail. Éteint, tout le site revient au style sombre d’origine."
          effets={[
            'Tout le site change d’allure : accueil, tarifs, espace parent et élève, administration, page d’attente de la maintenance.',
            'La page de connexion suit aussi : le serveur d’identité relit ce réglage à chaque affichage, avec au plus quinze secondes de retard.',
            'Tant qu’il est allumé, le site reste en sombre, même pour ceux qui ont choisi le thème clair ; leur choix revient dès que vous l’éteignez.',
            'Vous le voyez tout de suite ; les autres visiteurs, au prochain chargement de page.',
          ]}
          note="RÉVERSIBLE À TOUT MOMENT. L’ancien style n’a pas été modifié : Blue Sky s’ajoute par-dessus. L’éteindre rend exactement le site d’avant, page de maintenance comprise."
        >
          {/* LES DÉCLINAISONS — Camara, le 29/09/2026 : « plusieurs modes de
              design dans la même carte que le toggle Blue Sky ». Des boutons
              exclusifs, comme un choix de thème : un seul actif, et
              « Classique » ramène le bleu d'origine de Blue Sky. */}
          <div className="mode__declinaisons">
            <p className="mode__declinaisons-titre">Déclinaison</p>
            <div className="mode__declinaisons-choix" role="radiogroup" aria-label="Déclinaison de Blue Sky">
              {[{ cle: null, libelle: 'Classique' }, ...SAISONS].map((option) => {
                const choisie = reglages.blueSky && (reglages.styleSaison ?? null) === option.cle;
                return (
                  <button
                    key={option.cle ?? 'classique'}
                    type="button"
                    role="radio"
                    aria-checked={choisie}
                    className={`mode__declinaison${choisie ? ' mode__declinaison--choisie' : ''}`}
                    data-saison-apercu={option.cle ?? 'classique'}
                    disabled={!lus || envoi === 'STYLE_SAISON'}
                    onClick={() => choisirSaison(option.cle)}
                  >
                    <span className="mode__declinaison-pastille" aria-hidden="true" />
                    {option.libelle}
                  </button>
                );
              })}
            </div>
            <p className="mode__declinaisons-aide">
              {reglages.blueSky
                ? 'Chacune garde la structure de Blue Sky et change son fond et ses couleurs, sur tout le site et la page de connexion. Tout le monde la voit aussitôt.'
                : 'Choisir une déclinaison allume Blue Sky.'}
            </p>
          </div>
        </Interrupteur>

        {/* LE MODE DÉVELOPPEUR — voulu par Camara le 16/09/2026 pour ses
            essais. Un interrupteur et NON une phrase tapée en séance : une
            phrase se répète, et le premier élève qui l'apprend obtient les
            réponses toutes faites. */}
        {/* LES LANGUES, UNE PAR UNE — Camara, le 04/10/2026 : « on peut les
            implémenter, je teste en local, et je ne les affiche pas en prod
            tant qu'elles ne sont pas prêtes ». La liste vient du serveur : une
            langue n'a son interrupteur que le jour où sa matière existe en
            base. L'anglais n'y est pas — c'est la langue de tous les élèves. */}
        {reglages.langues.length > 0 && (
          <section className="mode">
            <div className="mode__entete">
              <div>
                <h3 className="mode__titre">Les langues</h3>
                <p className="mode__description">
                  Chaque langue se montre ou se cache à part. Éteinte, elle
                  n’apparaît plus nulle part : ni dans les matières de l’élève,
                  ni dans ses options, ni son professeur sur la page d’accueil.
                  Rien n’est supprimé — la rallumer rend tout tel quel.
                </p>
              </div>
            </div>

            {reglages.langues.map(({ code, libelle, actif }) => (
              <div className="mode__sous-reglage" key={code}>
                <div className="mode__sous-texte">
                  <strong>{libelle}</strong>
                  <span>
                    {actif
                      ? 'Visible : la matière, son professeur et son option sont proposés.'
                      : 'Masquée : personne ne la voit, et aucun cours ne peut s’ouvrir.'}
                  </span>
                </div>

                <button
                  type="button"
                  className={`bascule bascule--mini ${actif ? 'bascule--active' : ''}`}
                  onClick={() => basculerLangue(code)}
                  disabled={envoi === `LANGUE_${code}` || !lus}
                  role="switch"
                  aria-checked={actif}
                  aria-label={libelle}
                >
                  <span className="bascule__piste">
                    <span className="bascule__bouton" />
                  </span>
                </button>
              </div>
            ))}

            <p className="mode__note">
              Un élève qui avait coché une langue garde son choix : il le
              retrouve quand elle est rallumée. Les écrans déjà ouverts se
              mettent à jour à leur prochain chargement.
            </p>
          </section>
        )}

        {/* LES JEUX, CYCLE PAR CYCLE — Camara, le 20/09/2026.

            TROIS INTERRUPTEURS ET NON UN. Les jeux du primaire — très visuels,
            à manipuler, glisser-déposer — n’ont rien à voir avec ceux du
            lycée, et n’arriveront pas en même temps. Un drapeau unique aurait
            forcé à tout ouvrir ou tout fermer : on veut pouvoir livrer le
            primaire seul et laisser les deux autres portes closes.

            LE CYCLE DE L’ENFANT DÉCIDE, pas son âge ni sa classe écrite à la
            main : c’est `NiveauScolaire.Cycle`, la même colonne qui pilote
            déjà le ton du professeur. */}
        <section className="mode">
          <div className="mode__entete">
            <div>
              <h3 className="mode__titre">Les jeux</h3>
              <p className="mode__description">
                Le bouton « Mes jeux » s’affiche-t-il ? Chaque cycle décide pour
                lui-même : un enfant ne voit que l’interrupteur du sien. Éteint,
                la porte n’existe pas — ni sur son accueil, ni dans la vue de
                son parent.
              </p>
            </div>
          </div>

          {CYCLES_JEUX.map(({ cle, champ, titre, classes }) => (
            <div className="mode__sous-reglage" key={cle}>
              <div className="mode__sous-texte">
                <strong>{titre}</strong>
                <span>
                  {reglages[champ]
                    ? `Les ${classes} voient le bouton « Mes jeux ».`
                    : `Les ${classes} ne voient aucun bouton « Mes jeux ».`}
                </span>
              </div>

              <button
                type="button"
                className={`bascule bascule--mini ${reglages[champ] ? 'bascule--active' : ''}`}
                onClick={() => basculer(cle, champ)}
                disabled={envoi === cle || !lus}
                role="switch"
                aria-checked={reglages[champ]}
                aria-label={titre}
              >
                <span className="bascule__piste">
                  <span className="bascule__bouton" />
                </span>
              </button>
            </div>
          ))}

          <p className="mode__note">
            Cacher le bouton n’est pas une garde : la page reste joignable par
            son adresse. C’est un réglage d’affichage, pas une sécurité — il n’y
            a rien à protéger derrière.
          </p>
        </section>

        <Interrupteur
          titre="Mode développeur"
          actif={reglages.modeDeveloppeur}
          connu={lus}
          occupe={envoi === 'MODE_DEVELOPPEUR'}
          onBasculer={() => basculer('MODE_DEVELOPPEUR', 'modeDeveloppeur')}
          description="Pour essayer le produit : le professeur exécute ce qu’on lui demande, sans discuter. Il relance un exercice autant de fois qu’on veut, même si le précédent n’a pas de réponse, et il donne les réponses si on les lui demande."
          effets={[
            'Une nouvelle dictée ou une nouvelle compréhension orale part immédiatement, sans qu’il réclame de finir la précédente.',
            'Plus d’insistance, d’ultimatum ni de recadrage : aucune demande n’est discutée.',
            'Il donne la réponse d’un exercice si on la lui demande — la règle qui l’interdit protège un élève qui apprend, pas un essai.',
            'Les blocs techniques gardent leur forme exacte : c’est ce qu’on vient vérifier.',
            'Le changement vaut pour le prochain message, y compris dans un cours déjà commencé.',
          ]}
          note="À ÉTEINDRE APRÈS VOS ESSAIS. Allumé, il vaut pour TOUTES les séances en cours, y compris celles de vrais élèves : ceux-là obtiendraient les réponses de leurs exercices. C’est un outil d’essai, pas un réglage de confort — et c’est pour cela qu’il vit ici, hors de portée d’un élève, plutôt que dans une phrase à taper en cours."
        />

        <Interrupteur
          titre="Mode test"
          actif={reglages.modeTest}
          connu={lus}
          occupe={envoi === 'MODE_TEST'}
          onBasculer={() => basculer('MODE_TEST', 'modeTest')}
          description="Le service passe en accès privé : personne ne peut créer de compte, et seuls les comptes déjà existants se connectent."
          effets={[
            'Le lien « Tarifs » disparaît de la barre de navigation.',
            'La connexion par Google est masquée.',
            'Le lien « Créer un compte parent » est masqué.',
            'Le lien « Mot de passe oublié ? » est masqué.',
            'La page de connexion affiche « Bêta test · accès privé ».',
          ]}
          note="La page de connexion est servie par le serveur d'identité : il relit ce réglage à chaque affichage, le changement est donc immédiat pour lui aussi."
        />

        <Interrupteur
          titre="Compte de démonstration"
          actif={reglages.compteTest}
          connu={lus}
          occupe={envoi === 'COMPTE_TEST_ACTIF'}
          onBasculer={() => basculer('COMPTE_TEST_ACTIF', 'compteTest')}
          description="Le compte test@mimia.fr, pour montrer le produit avec les yeux d'un parent ordinaire."
          effets={[
            'Éteint, la connexion est refusée comme si l’adresse n’existait pas.',
            'Le compte n’est jamais supprimé : historique, fiches et évaluations sont conservés.',
            'Le rallumer le rend utilisable immédiatement, avec tout son contenu.',
          ]}
          note="Le message d’erreur est volontairement le même que pour une adresse inconnue : dire « ce compte est désactivé » confirmerait qu’il existe, et donnerait une adresse valide à qui cherche."
        />

        <Interrupteur
          titre="Essai gratuit"
          actif={reglages.essaisOuverts}
          connu={lus}
          occupe={envoi === 'ESSAIS_OUVERTS'}
          onBasculer={() => basculer('ESSAIS_OUVERTS', 'essaisOuverts')}
          description="Les trente minutes offertes aux nouveaux venus. Fermées, le site continue de vendre normalement — seule la porte d’entrée gratuite disparaît."
          effets={[
            '« Commencer gratuitement » devient « S’inscrire maintenant » sur l’accueil.',
            'Le bloc « Essayez d’abord » disparaît de la page des tarifs.',
            'Plus aucun chemin n’ouvre d’essai, y compris en appelant l’API directement.',
            'Les essais DÉJÀ en cours continuent jusqu’à leur terme.',
          ]}
          note="Les essais en cours ne sont pas interrompus : couper un essai commencé reviendrait à retirer une promesse déjà faite. Le réglage ne ferme que la porte d’entrée."
        />

        <Interrupteur
          titre="Tâches de fond"
          actif={reglages.tachesDeFond}
          connu={lus}
          occupe={envoi === 'TACHES_DE_FOND_ACTIVES'}
          onBasculer={() => basculer('TACHES_DE_FOND_ACTIVES', 'tachesDeFond')}
          description="Le coupe-circuit de la facture. Les travaux automatiques qui appellent le modèle en dehors des cours : lire les planches importées, y placer les repères, archiver le texte des documents envoyés. Utiles, mais jamais urgents."
          effets={[
            'Les planches importées restent sans description ni repères tant que c’est éteint.',
            'Les documents envoyés par les élèves restent PARFAITEMENT lisibles : le professeur reçoit le document lui-même, pas une transcription.',
            'Seul son archivage attend — le texte relevé ne sert qu’après l’effacement du fichier, trois jours plus tard, si l’élève rouvre la vieille conversation.',
            'Rien n’est perdu : un document non transcrit n’est jamais effacé. Il occupe de la place, voilà tout.',
            'Aucun appel au modèle n’est fait hors des cours.',
          ]}
          note="Prend effet au prochain travail — pas besoin de redéployer. Plus rien ne tourne au chronomètre : les planches dorment jusqu’au prochain import, l’archivage des documents jusqu’au prochain envoi d’élève. Éteint, l’arriéré attend sans rien coûter, et repartira au prochain réveil."
        />
      </div>

      <PlaceBase />

      <FilesPlanches />

      <CatalogueStripe />
    </div>
  );
}
